import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/lib/api';
import { Thermometer, CloudRain, Wind, Droplets, MapPin, Loader2 } from 'lucide-react';

// Open-Meteo WMO weather-code → emoji + short label
const WEATHER_CODE = {
  0: ['☀️', 'clear'], 1: ['🌤️', 'mostly clear'], 2: ['⛅', 'partly cloudy'], 3: ['☁️', 'overcast'],
  45: ['🌫️', 'fog'], 48: ['🌫️', 'rime fog'],
  51: ['🌦️', 'drizzle'], 53: ['🌦️', 'drizzle'], 55: ['🌦️', 'dense drizzle'],
  61: ['🌧️', 'light rain'], 63: ['🌧️', 'rain'], 65: ['🌧️', 'heavy rain'],
  71: ['🌨️', 'snow'], 73: ['🌨️', 'snow'], 75: ['❄️', 'heavy snow'],
  80: ['🌦️', 'showers'], 81: ['🌧️', 'showers'], 82: ['⛈️', 'violent showers'],
  95: ['⛈️', 'thunderstorm'], 96: ['⛈️', 'thunderstorm w/ hail'], 99: ['⛈️', 'severe thunderstorm'],
};

const WeatherBar = ({ farm }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!farm) { setData(null); return; }
    let cancelled = false;
    setLoading(true); setErr(null);
    axios.get(`${API}/weather/nowcast`, { params: { lat: farm.lat, lon: farm.lon } })
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setErr('offline'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [farm?.id, farm?.lat, farm?.lon]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!farm) return null;

  const [emoji, label] = data ? (WEATHER_CODE[data.weather_code] || ['🌡️', '']) : ['🌡️', ''];

  return (
    <div
      data-testid="weather-bar"
      className="fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-icrisat-green/25 shadow-[0_-4px_14px_rgba(0,0,0,0.05)] pb-safe"
    >
      <div className="max-w-[1400px] mx-auto px-3 sm:px-6 py-2 flex items-center gap-3 sm:gap-4 overflow-x-auto text-sm">
        <div className="flex items-center gap-1.5 text-icrisat-green shrink-0">
          <MapPin className="h-4 w-4" />
          <span className="font-medium truncate max-w-[120px] sm:max-w-none">{farm.name}</span>
        </div>

        {loading && (
          <span className="text-muted-foreground flex items-center gap-1 shrink-0">
            <Loader2 className="h-3 w-3 animate-spin" /> loading weather…
          </span>
        )}
        {err && (
          <span className="text-muted-foreground shrink-0 text-xs">weather unavailable</span>
        )}
        {data && (
          <>
            <span className="text-lg shrink-0" title={label}>{emoji}</span>
            <Stat icon={Thermometer} label="Now" value={`${round1(data.temp_c)}°C`} />
            <Stat icon={Thermometer} label="Max" value={`${round1(data.today_tmax_c)}°`} className="hidden xs:flex" />
            <Stat icon={Thermometer} label="Min" value={`${round1(data.today_tmin_c)}°`} className="hidden xs:flex" />
            <Stat icon={CloudRain} label="Today rain" value={`${round1(data.today_rain_mm)} mm`} />
            <Stat icon={Wind} label="Wind" value={`${round1(data.wind_kmh)} km/h`} className="hidden sm:flex" />
            <Stat icon={Droplets} label="Humidity" value={`${data.humidity_pct ?? '—'}%`} className="hidden md:flex" />
          </>
        )}
        <span className="hidden md:inline ml-auto text-xs text-muted-foreground">Open-Meteo · updated live</span>
      </div>
    </div>
  );
};

const Stat = ({ icon: Icon, label, value, className = '' }) => (
  <div className={`flex items-center gap-1 shrink-0 ${className}`}>
    <Icon className="h-3.5 w-3.5 text-icrisat-earth" />
    <span className="text-xs text-icrisat-earth">{label}</span>
    <span className="font-medium text-icrisat-green">{value}</span>
  </div>
);

const round1 = (v) => (typeof v === 'number' && Number.isFinite(v) ? Math.round(v * 10) / 10 : '—');

export default WeatherBar;
